import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,

  Checkbox,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Calayout";

const AccessControlPage = () => {
 
  const [roles, setRoles] = useState([
    {
      id: 1,
      name: "Admin",
      description: "Full system access",
      permissions: {
        dashboard: ["view", "edit"],
        clients: ["view", "create", "edit", "delete"],
        reports: ["view", "generate"],
        team: ["view", "manage"],
        financials: ["view", "edit"],
      },
    },
    {
      id: 2,
      name: "CA",
      description: "Chartered Accountant access",
      permissions: {
        dashboard: ["view"],
        clients: ["view", "edit"],
        reports: ["view"],
        team: ["view"],
        financials: ["view"],
      },
    },
  ]);


  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [currentRole, setCurrentRole] = useState({
    name: "",
    description: "",
    permissions: {},
  });

 
  const permissionSections = [
    {
      name: "dashboard",
      label: "Dashboard",
      actions: ["view", "edit"],
    },
    {
      name: "clients",
      label: "Clients",
      actions: ["view", "create", "edit", "delete"],
    },
    {
      name: "reports",
      label: "Reports",
      actions: ["view", "generate"],
    },
    {
      name: "team",
      label: "Team Management",
      actions: ["view", "manage"],
    },
    {
      name: "financials",
      label: "Financials",
      actions: ["view", "edit"],
    },
  ];

  
  const handleSaveRole = () => {
    if (currentRole.id) {
     
      setRoles(
        roles.map((role) => (role.id === currentRole.id ? currentRole : role))
      );
    } else {
     
      setRoles([...roles, { ...currentRole, id: roles.length + 1 }]);
    }
    setOpenRoleDialog(false);
   
    setCurrentRole({ name: "", description: "", permissions: {} });
  };

 
  const handleDeleteRole = (id) => {
    setRoles(roles.filter((role) => role.id !== id));
  };

 
  const togglePermission = (section, action) => {
    setCurrentRole((prev) => {
      const sectionPermissions = prev.permissions[section] || [];
      const updatedPermissions = {
        ...prev.permissions,
        [section]: sectionPermissions.includes(action)
          ? sectionPermissions.filter((a) => a !== action)
          : [...sectionPermissions, action],
      };
      return { ...prev, permissions: updatedPermissions };
    });
  };

 
  const renderRoleDialog = () => (
    <Dialog
      open={openRoleDialog}
      onClose={() => setOpenRoleDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{currentRole.id ? "Edit Role" : "Add New Role"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Role Name"
              value={currentRole.name}
              onChange={(e) =>
                setCurrentRole({
                  ...currentRole,
                  name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Role Description"
              value={currentRole.description}
              onChange={(e) =>
                setCurrentRole({
                  ...currentRole,
                  description: e.target.value,
                })
              }
            />
          </Grid>

          
          {permissionSections.map((section) => (
            <Grid item xs={12} key={section.name}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{section.label} Permissions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {section.actions.map((action) => (
                      <FormControlLabel
                        key={action}
                        control={
                          <Checkbox
                            checked={
                              currentRole.permissions[section.name]?.includes(
                                action
                              ) || false
                            }
                            onChange={() =>
                              togglePermission(section.name, action)
                            }
                          />
                        }
                        label={action.charAt(0).toUpperCase() + action.slice(1)}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenRoleDialog(false)}>Cancel</Button>
        <Button onClick={handleSaveRole} variant="contained" color="primary">
          Save Role
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Access Control Management</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setCurrentRole({ name: "", description: "", permissions: {} });
              setOpenRoleDialog(true);
            }}
          >
            Add Role
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Role Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell>{role.name}</TableCell>
                      <TableCell>{role.description}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {Object.entries(role.permissions).map(
                            ([section, actions]) => (
                              <Chip
                                key={section}
                                label={`${section}: ${actions.join(", ")}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex" }}>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setCurrentRole(role);
                              setOpenRoleDialog(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteRole(role.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {renderRoleDialog()}
      </Box>
    </Layout>
  );
};

export default AccessControlPage;
